import { Edit } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip as MuiChip,
  Divider,
  Grid,
  MenuItem,
  TablePagination,
  TextField,
  Typography
} from '@mui/material'
import { Chip } from 'components/chips'
import { GreenText } from 'components'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { VehicleDamageDto } from 'dtos'
import { DamageParameters } from 'parameters/damageParameters'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from 'utils'
import DamageHeroCard from './DamageHeroCard'
import { DamagePhoto1Dto } from 'dtos/damagePhoto1Dto'
import { DamagePhoto2Dto } from 'dtos/damagePhoto2Dto'
import { DamagePhoto3Dto } from 'dtos/damagePhoto3Dto'
import moment from 'moment'

const sessionStorageKey = 'damage:parameters'

export interface DamagePhotosProps {
  damagePhoto1: DamagePhoto1Dto
  setDamagePhoto1: React.Dispatch<React.SetStateAction<DamagePhoto1Dto>>
  damagePhoto2: DamagePhoto2Dto
  setDamagePhoto2: React.Dispatch<React.SetStateAction<DamagePhoto2Dto>>
  damagePhoto3: DamagePhoto3Dto
  setDamagePhoto3: React.Dispatch<React.SetStateAction<DamagePhoto3Dto>>
}

export default function Damage() {
  const layout = useLayout()
  const navigate = useNavigate()

  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')

  const defaultParameters: DamageParameters = {
    page: 0,
    pageSize: 10,
    orderBy: 'initialDamageDate',
    order: 'asc',
    damageStatus: 'Unresolved'
  }

  const [isGettingDamages, setIsGettingDamages] = useState<boolean>(true)
  const [parameters, setParameters] = useState<DamageParameters>(
    sessionStorage.getItem(sessionStorageKey) ? JSON.parse(sessionStorage.getItem(sessionStorageKey)!) : defaultParameters
  )
  const [damages, setDamages] = useState<VehicleDamageDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  const getDamages = useDebounce((parameters: DamageParameters) => {
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(parameters))

    setIsGettingDamages(true)
    controllers
      .getDamages(parameters)
      .then(response => {
        setDamages(response.value)
        setTotalCount(response.totalCount ?? 0)
      })
      .finally(() => {
        setIsGettingDamages(false)
      })
  }, 300)

  useEffect(() => {
    getDamages(parameters)
  }, [getDamages, parameters])

  useEffect(() => {
    layout.setTitle('Damage')
    return () => layout.setTitle('')
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(isGettingDamages)
  }, [layout, isGettingDamages])

  const displayCount = totalCount

  return (
    <>
      <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography fontWeight='bold' variant='h5'>
                  Damage ({displayCount})
                </Typography>

                <Button
                  color='primary'
                  onClick={() => {
                    // setDamage(new DamageDto())
                    // setDamageAddEditDialogOpen(true)
                    navigate('/damage/add-damage')
                  }}
                  variant='contained'
                >
                  ADD DAMAGE
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={2} alignItems='center'>
                <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
                  <Grid item xs={12} sm={true} container spacing={1} alignItems='center'>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        fullWidth
                        label='Filter Field'
                        onChange={e => {
                          setFilterField(e.target.value)
                          setFilterValue('')
                        }}
                        select
                        sx={{ minWidth: 200 }}
                        value={filterField || ''}
                      >
                        <MenuItem value='damageStatus'>Status</MenuItem>

                        <MenuItem value='initialDamageDate'>Initial Damage Date</MenuItem>

                        <MenuItem value='exporter'>Exporter</MenuItem>

                        <MenuItem value='buyer'>Buyer</MenuItem>

                        <MenuItem value='vin'>VIN</MenuItem>

                        <MenuItem value='year'>Year</MenuItem>

                        <MenuItem value='make'>Make</MenuItem>

                        <MenuItem value='model'>Model</MenuItem>
                      </TextField>
                    </Grid>

                    {['', 'exporter', 'buyer', 'vin', 'year', 'make', 'model'].includes(filterField) && (
                      <Grid item xs={12} sm='auto'>
                        <TextField
                          disabled={!filterField}
                          label='Filter Value'
                          onChange={e => {
                            setFilterValue(e.target.value)
                          }}
                          value={filterValue || ''}
                        />
                      </Grid>
                    )}

                    {filterField === 'damageStatus' && (
                      <Grid item xs={12} sm='auto'>
                        <TextField
                          fullWidth
                          label='Filter Value'
                          onChange={e => {
                            setFilterValue(e.target.value)
                          }}
                          select
                          sx={{ minWidth: 200 }}
                          value={filterValue || ''}
                        >
                          <MenuItem value='Resolved'>
                            <Chip variant='green'>Resolved</Chip>
                          </MenuItem>

                          <MenuItem value='Unresolved'>
                            <Chip variant='red'>Unresolved</Chip>
                          </MenuItem>
                        </TextField>
                      </Grid>
                    )}

                    {filterField === 'initialDamageDate' && (
                      <>
                        <Grid item xs={12} sm='auto'>
                          <TextField
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                            label='Filter Date From'
                            onChange={e => {
                              setFilterValueFrom(e.target.value)
                            }}
                            type='date'
                            value={filterValueFrom}
                          />
                        </Grid>

                        <Grid item xs={12} sm='auto'>
                          <TextField
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                            label='Filter Date To'
                            onChange={e => {
                              setFilterValueTo(e.target.value)
                            }}
                            type='date'
                            value={filterValueTo}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12} sm='auto'>
                      <Button
                        color='inherit'
                        disabled={
                          !filterField ||
                          (filterField === 'initialDamageDate' ? !filterValueFrom || !filterValueTo : !filterValue)
                        }
                        variant='contained'
                        onClick={() => {
                          if (filterField === 'initialDamageDate') {
                            setParameters({
                              ...parameters,
                              initialDamageDateFrom: filterValueFrom,
                              initialDamageDateTo: filterValueTo
                            })
                          } else {
                            setParameters({
                              ...parameters,
                              [filterField]: filterValue
                            })
                          }

                          setFilterField('')
                          setFilterValue('')
                          setFilterValueFrom('')
                          setFilterValueTo('')
                        }}
                      >
                        APPLY FILTER
                      </Button>
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <Button
                        color='secondary'
                        disabled={
                          !Boolean(
                            parameters.damageStatus ||
                              parameters.initialDamageDateFrom ||
                              parameters.initialDamageDateTo ||
                              parameters.exporter ||
                              parameters.buyer ||
                              parameters.vin ||
                              parameters.year ||
                              parameters.make ||
                              parameters.model
                          )
                        }
                        variant='text'
                        onClick={() => {
                          setParameters({
                            ...parameters,
                            damageStatus: undefined,
                            initialDamageDateFrom: undefined,
                            initialDamageDateTo: undefined,
                            exporter: undefined,
                            buyer: undefined,
                            vin: undefined,
                            year: undefined,
                            make: undefined,
                            model: undefined
                          })
                        }}
                      >
                        RESET FILTERS
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>

                {parameters.damageStatus && (
                  <Grid item xs={12} sm='auto'>
                    <MuiChip
                      color='secondary'
                      label={`Status is "${parameters.damageStatus}"`}
                      onDelete={() => {
                        setParameters({
                          ...parameters,
                          damageStatus: undefined
                        })
                      }}
                      variant='outlined'
                    />
                  </Grid>
                )}

                {parameters.initialDamageDateFrom && parameters.initialDamageDateTo && (
                  <Grid item xs={12} sm='auto'>
                    <MuiChip
                      color='secondary'
                      label={`Initial Damage Date from "${moment(parameters.initialDamageDateFrom).format(
                        'M/D/yyyy'
                      )}" to "${moment(parameters.initialDamageDateTo).format('M/D/yyyy')}"`}
                      onDelete={() => {
                        setParameters({
                          ...parameters,
                          initialDamageDateFrom: undefined,
                          initialDamageDateTo: undefined
                        })
                      }}
                      variant='outlined'
                    />
                  </Grid>
                )}

                {parameters.exporter && (
                  <Grid item xs={12} sm='auto'>
                    <MuiChip
                      color='secondary'
                      label={`Exporter Company contains "${parameters.exporter}"`}
                      onDelete={() => {
                        setParameters({
                          ...parameters,
                          exporter: undefined
                        })
                      }}
                      variant='outlined'
                    />
                  </Grid>
                )}

                {parameters.buyer && (
                  <Grid item xs={12} sm='auto'>
                    <MuiChip
                      color='secondary'
                      label={`Buyer Company contains "${parameters.buyer}"`}
                      onDelete={() => {
                        setParameters({
                          ...parameters,
                          buyer: undefined
                        })
                      }}
                      variant='outlined'
                    />
                  </Grid>
                )}

                {parameters.vin && (
                  <Grid item xs={12} sm='auto'>
                    <MuiChip
                      color='secondary'
                      label={`VIN contains "${parameters.vin}"`}
                      onDelete={() => {
                        setParameters({
                          ...parameters,
                          vin: undefined
                        })
                      }}
                      variant='outlined'
                    />
                  </Grid>
                )}

                {parameters.year && (
                  <Grid item xs={12} sm='auto'>
                    <MuiChip
                      color='secondary'
                      label={`Year is ${parameters.year}`}
                      onDelete={() => {
                        setParameters({
                          ...parameters,
                          year: undefined
                        })
                      }}
                      variant='outlined'
                    />
                  </Grid>
                )}

                {parameters.make && (
                  <Grid item xs={12} sm='auto'>
                    <MuiChip
                      color='secondary'
                      label={`Make contains "${parameters.make}"`}
                      onDelete={() => {
                        setParameters({
                          ...parameters,
                          make: undefined
                        })
                      }}
                      variant='outlined'
                    />
                  </Grid>
                )}

                {parameters.model && (
                  <Grid item xs={12} sm='auto'>
                    <MuiChip
                      color='secondary'
                      label={`Model contains "${parameters.model}"`}
                      onDelete={() => {
                        setParameters({
                          ...parameters,
                          model: undefined
                        })
                      }}
                      variant='outlined'
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {damages.map(damage => (
          <Grid item xs={12} key={damage.vehicleDamageGuid}>
            <DamageHeroCard damage={damage}>
              <Grid item xs={12} sm='auto'>
                <Button
                  endIcon={<Edit />}
                  onClick={() => {
                    navigate(`/vehicles/${damage.vehicleGuid}/damage`)
                  }}
                  variant='text'
                >
                  EDIT
                </Button>
              </Grid>
            </DamageHeroCard>
          </Grid>
        ))}

        <Grid item xs={12}>
          <GreenText variant='outlined'>
            {`**Damage List Screen:**
                    
The Damage List Screen is used by a Registered Importer team member to record vehicle damage observed in the Import Branch parking lot. Once the damage is observed, the user navigates to this screen to 
log the details.

Vehicles only appear in the Damage List once, regardless of how many damage records are associated with the vehicle, even if those damages are resolved.
                    
**Add Damage Button:**
                                        
Clicking “Add Damage” navigates the user to the “Add Damage – Select Vehicle” screen.
                    
**Filter Field Drop-Down:**
                                        
Clicking the drop-down displays a list of available columns to filter by:

- Status
- Initial Damage Date
- Exporter
- Buyer
- VIN
- Year
- Make
- Model
                                        
**Filter Value:**
                                        
The "Filter Value" field dynamically adjusts based on the selected column in the "Filter Field Drop-Down":

- Resolved: Displays vehicles where all damage records are resolved.
- Unresolved: Displays vehicles where at least one damage record is unresolved. 
- Initial Damage Date:  Displays Filter Date From and Filter Date To.
- Exporter: Displays a free-type filter field
- Buyer: Displays a free-type filter field
- VIN: Displays a free-type filter field
- Year: Displays a free-type filter field
- Make: Displays a free-type filter field
- Model: Displays a free-type filter field
                    
**Apply Filter Button:**
                                        
Clicking this button applies the selected filtering options and updates the displayed data.

**Reset Filters:**

Clicking this button clears all applied filtering options, returning the data view to its default state.
                    
**VIN:**

Displays the VIN of the vehicle with the reported damage.

**Resolved Chip:**

A Resolved chip is displayed if the vehicle’s reported damage has been resolved.

**Unresolved Chip:**

An Unresolved chip is displayed if the vehicle’s reported damage has not been resolved.

**Initial Damage Date:**

Displays the Initial Damage Date, which is the date the first damage record was added for the vehicle.

**Updated:**

Displays the Updated date, indicating the most recent change made to any damage record associated with the vehicle.

**Exporter:**

Displays the Exporter associated with the vehicle.

**Buyer:**

Displays the Buyer associated with the vehicle.

**Importer Stock #:**

Displays the Importer Stock # associated with the vehicle.

**Year:**

Displays the Year of the vehicle.

**Make:**

Displays the Make of the vehicle.

**Model:**

Displays the Model of the vehicle.

                    `}
          </GreenText>
        </Grid>

        <Grid item xs={12}>
          <TablePagination
            component='div'
            count={displayCount}
            onPageChange={(_, page: number) => {
              setParameters({
                ...parameters,
                page
              })
            }}
            onRowsPerPageChange={e => {
              setParameters({
                ...parameters,
                page: 0,
                pageSize: e.target.value as unknown as number
              })
            }}
            page={parameters.page}
            rowsPerPage={parameters.pageSize}
            labelRowsPerPage={
              <Grid container spacing={1}>
                <Grid item>Order By:</Grid>

                <Grid item>
                  <TextField
                    InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                    onChange={e => {
                      setParameters({ ...parameters, orderBy: e.target.value })
                    }}
                    select
                    size='small'
                    value={parameters.orderBy}
                    variant='standard'
                  >
                    <MenuItem value='initialDamageDate'>Initial Damage Date</MenuItem>

                    <MenuItem value='updatedDate'>Updated</MenuItem>

                    <MenuItem value='damageStatus'>Status</MenuItem>

                    <MenuItem value='vin'>VIN</MenuItem>

                    <MenuItem value='year'>Year</MenuItem>

                    <MenuItem value='make'>Make</MenuItem>

                    <MenuItem value='model'>Model</MenuItem>

                    <MenuItem value='importerStockNumber'>Importer Stock #</MenuItem>

                    <MenuItem value='exporterCompany'>Exporter</MenuItem>

                    <MenuItem value='buyerCompany'>Buyer</MenuItem>
                  </TextField>
                </Grid>

                <Grid item>
                  <TextField
                    InputProps={{ disableUnderline: true, sx: { fontSize: 'inherit', pl: 1 } }}
                    onChange={e => {
                      setParameters({ ...parameters, order: e.target.value as 'asc' | 'desc' })
                    }}
                    select
                    size='small'
                    value={parameters.order}
                    variant='standard'
                  >
                    <MenuItem value='asc'>A-Z</MenuItem>

                    <MenuItem value='desc'>Z-A</MenuItem>
                  </TextField>
                </Grid>

                <Grid item sx={{ alignSelf: 'stretch' }}>
                  <Divider orientation='vertical' />
                </Grid>

                <Grid item>Cards Per Page:</Grid>
              </Grid>
            }
            rowsPerPageOptions={[
              { label: '5', value: 5 },
              { label: '10', value: 10 },
              { label: '25', value: 25 },
              { label: '50', value: 50 },
              { label: '100', value: 100 }
            ]}
            SelectProps={{ fullWidth: false }}
          />
        </Grid>
      </Grid>
    </>
  )
}
